/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: AI Assistant
 🏢 Company: Ring2All LLC
 📄 File: SelectVIPList.tsx
 🗓️ Created: 2025-01-20
 🧩 Description:
    Reusable component for selecting a VIP List with search functionality.

 🔧 Notes:
    - Searchable select component
    - Single selection
    - Filters by VIP list name

 🔁 Last Updated: 2025-01-20 by AI Assistant
────────────────────────────────────────────────────────────────*/

import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';

interface VIPList {
  id: number;
  name: string;
  enabled?: boolean;
}

interface SelectVIPListProps {
  value: number | null;
  onChange: (value: number | null) => void;
  vipLists?: VIPList[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  error?: boolean;
}

export default function SelectVIPList({
  value,
  onChange,
  vipLists = [],
  placeholder,
  disabled = false,
  className = '',
  error = false
}: SelectVIPListProps) {
  const { t } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const defaultPlaceholder = placeholder || t('queues.form.fields.vipListIds.none');

  // Get selected VIP list
  const selectedVIPList = value && vipLists ? vipLists.find(list => list.id === value) : null;

  // Filter VIP lists based on search
  const filteredVIPLists = (vipLists || []).filter(list =>
    list.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when dropdown opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (listId: number) => {
    onChange(listId);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setIsOpen(false);
    setSearchQuery('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-left border rounded-md
          bg-background text-foreground
          ${error ? 'border-red-500' : 'border-input'}
          hover:bg-accent hover:text-accent-foreground
          focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed
          flex items-center justify-between
          transition-colors text-sm
        `}
      >
        <span className={selectedVIPList ? '' : 'text-muted-foreground'}>
          {selectedVIPList ? selectedVIPList.name : defaultPlaceholder}
        </span>
        <div className="flex items-center gap-1">
          {selectedVIPList && (
            <span
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors cursor-pointer px-1"
            >
              ×
            </span>
          )}
          <ChevronDown
            className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </div>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('queues.form.fields.vipListIds.searchPlaceholder')}
              className="w-full px-3 py-2 text-sm bg-background border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto p-1">
            {filteredVIPLists.length > 0 ? (
              filteredVIPLists.map((list) => (
                <button
                  key={list.id}
                  type="button"
                  onClick={() => handleSelect(list.id)}
                  className={`
                    w-full px-3 py-2 text-left rounded-md
                    hover:bg-accent hover:text-accent-foreground
                    flex items-center justify-between
                    transition-colors
                    ${value === list.id ? 'bg-accent text-accent-foreground' : ''}
                  `}
                >
                  <div className="flex-1">
                    <div className="font-medium">{list.name}</div>
                  </div>
                  {value === list.id && (
                    <Check className="h-4 w-4 text-primary" />
                  )}
                </button>
              ))
            ) : (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                {t('queues.form.fields.vipListIds.noResults')}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

